import produce from 'immer';
import {
  GET_TASK_RULES_REQUEST,
  GET_TASK_RULES_SUCCESS,
  GET_TASK_RULES_FAILURE,
  GET_TASK_RULE_REQUEST,
  GET_TASK_RULE_SUCCESS,
  GET_TASK_RULE_FAILURE,
  CREATE_TASK_RULE_SUCCESS,
  UPDATE_TASK_RULE_SUCCESS,
  DELETE_TASK_RULE_SUCCESS,
  GET_RULE_CONDITIONS_SUCCESS,
  ADD_RULE_CONDITION_SUCCESS,
  DELETE_RULE_CONDITION_SUCCESS,
  GET_RULE_CLIENTS_SUCCESS,
  ADD_RULE_CLIENT_SUCCESS,
  DELETE_RULE_CLIENT_SUCCESS,
  GET_VALID_TRIGGER_FIELDS_REQUEST,
  GET_VALID_TRIGGER_FIELDS_SUCCESS,
  GET_VALID_TRIGGER_FIELDS_FAILURE,
  GET_TASK_TRIGGER_SERVICES_REQUEST,
  GET_TASK_TRIGGER_SERVICES_SUCCESS,
  GET_TASK_TRIGGER_SERVICES_FAILURE,
  PROBE_SEGMENT_TABLES_SUCCESS,
  GENERATE_TASKS_FOR_RULE_SUCCESS,
  CLEAR_CURRENT_RULE,
  CLEAR_VALID_TRIGGER_FIELDS,
  SET_RULE_FORM_FIELD,
  RESET_RULE_FORM,
} from './taskRules.types';

const initialState = {
  // Rule list
  rules: [],
  isLoadingRules: false,
  rulesError: null,

  // Current rule being viewed/edited
  currentRule: null,
  isLoadingCurrentRule: false,
  currentRuleError: null,

  // Conditions for current rule
  currentConditions: [],

  // Client inclusions for current rule
  currentClients: [],

  // Valid trigger fields
  validTriggerFields: [],
  isLoadingTriggerFields: false,

  // Trigger services (Task.TaskTriggerServiceGroup catalog, inactive rows included)
  triggerServices: [],
  isLoadingTriggerServices: false,

  // taskTriggerServiceGroupId -> true when the service offers at least one segment table (false when it offers none).
  // Filled by probeSegmentTables; kept for the session so the form does not re-probe on every visit.
  segmentCapableServices: {},

  // Form state for rule creation/editing
  ruleForm: {
    ruleName: '',
    description: '',
    triggerServiceId: null,
    isSegmentLevel: false,
    taskTypeId: null,
    taskImportanceId: null,
    serviceId: null,
    regardingId: null,
    letterTemplateId: null,
    dueDateDaysOffset: 0,
    dueDateBasedOnTrigger: false,
    dueDateTriggerFieldId: null,
    dueDateTriggerFieldName: null,
    applyToAllClients: true,
    isActive: false,
  },
};

export default produce((draft = initialState, action) => {
  switch (action.type) {
    // Get all rules
    case GET_TASK_RULES_REQUEST:
      draft.isLoadingRules = true;
      draft.rulesError = null;
      break;

    case GET_TASK_RULES_SUCCESS:
      draft.rules = action.response || [];
      draft.isLoadingRules = false;
      break;

    case GET_TASK_RULES_FAILURE:
      draft.isLoadingRules = false;
      draft.rulesError = action.error;
      break;

    // Get single rule
    case GET_TASK_RULE_REQUEST:
      draft.isLoadingCurrentRule = true;
      draft.currentRuleError = null;
      break;

    case GET_TASK_RULE_SUCCESS:
      draft.currentRule = action.response;
      draft.isLoadingCurrentRule = false;
      break;

    case GET_TASK_RULE_FAILURE:
      draft.isLoadingCurrentRule = false;
      draft.currentRuleError = action.error;
      break;

    // Create rule
    case CREATE_TASK_RULE_SUCCESS:
      draft.rules.push(action.response);
      draft.currentRule = action.response;
      break;

    // Update rule
    case UPDATE_TASK_RULE_SUCCESS: {
      const index = draft.rules.findIndex(r => r.taskRuleId === action.response.taskRuleId);
      if (index !== -1) {
        draft.rules[index] = action.response;
      }
      draft.currentRule = action.response;
      break;
    }

    // Delete rule
    case DELETE_TASK_RULE_SUCCESS: {
      const ruleId = action.meta?.taskRuleId;
      if (ruleId) {
        draft.rules = draft.rules.filter(r => r.taskRuleId !== ruleId);
      }
      break;
    }

    // Conditions
    case GET_RULE_CONDITIONS_SUCCESS:
      draft.currentConditions = action.response || [];
      break;

    case ADD_RULE_CONDITION_SUCCESS:
      draft.currentConditions.push(action.response);
      break;

    case DELETE_RULE_CONDITION_SUCCESS: {
      const conditionId = action.meta?.conditionId;
      if (conditionId) {
        draft.currentConditions = draft.currentConditions.filter(
          c => c.taskRuleTriggerConditionId !== conditionId
        );
      }
      break;
    }

    // Client inclusions
    case GET_RULE_CLIENTS_SUCCESS:
      draft.currentClients = action.response || [];
      break;

    case ADD_RULE_CLIENT_SUCCESS:
      draft.currentClients.push(action.response);
      break;

    case DELETE_RULE_CLIENT_SUCCESS: {
      const inclusionId = action.meta?.inclusionId;
      if (inclusionId) {
        draft.currentClients = draft.currentClients.filter(
          c => c.taskRuleClientInclusionId !== inclusionId
        );
      }
      break;
    }

    // Valid trigger fields
    case GET_VALID_TRIGGER_FIELDS_REQUEST:
      draft.isLoadingTriggerFields = true;
      break;

    case GET_VALID_TRIGGER_FIELDS_SUCCESS:
      draft.validTriggerFields = action.response || [];
      draft.isLoadingTriggerFields = false;
      break;

    case GET_VALID_TRIGGER_FIELDS_FAILURE:
      draft.isLoadingTriggerFields = false;
      draft.validTriggerFields = [];
      break;

    case CLEAR_VALID_TRIGGER_FIELDS:
      draft.validTriggerFields = [];
      draft.isLoadingTriggerFields = false;
      break;

    // Trigger services
    case GET_TASK_TRIGGER_SERVICES_REQUEST:
      draft.isLoadingTriggerServices = true;
      break;

    case GET_TASK_TRIGGER_SERVICES_SUCCESS:
      draft.triggerServices = action.response || [];
      draft.isLoadingTriggerServices = false;
      break;

    case GET_TASK_TRIGGER_SERVICES_FAILURE:
      draft.isLoadingTriggerServices = false;
      break;

    // Segment-table probe: any non-File field at segment level means the service has a segment table
    case PROBE_SEGMENT_TABLES_SUCCESS: {
      const triggerServiceId = action.context && action.context.triggerServiceId;
      if (triggerServiceId) {
        draft.segmentCapableServices[triggerServiceId] = (action.response || []).some(f => !f.isFileTable);
      }
      break;
    }

    // Generate tasks
    case GENERATE_TASKS_FOR_RULE_SUCCESS:
      // Could update a tasksGenerated count if needed
      break;

    // Local state management
    case CLEAR_CURRENT_RULE:
      draft.currentRule = null;
      draft.currentConditions = [];
      draft.currentClients = [];
      draft.validTriggerFields = [];
      break;

    case SET_RULE_FORM_FIELD:
      if (action.payload?.field && draft.ruleForm.hasOwnProperty(action.payload.field)) {
        draft.ruleForm[action.payload.field] = action.payload.value;
      }
      break;

    case RESET_RULE_FORM:
      draft.ruleForm = initialState.ruleForm;
      draft.validTriggerFields = [];
      break;

    default:
      break;
  }

  return draft;
}, initialState);
